import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DollarSign, ArrowLeft, TrendingUp, ArrowDownLeft, ArrowUpRight, Clock, Wallet, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const WalletPage = () => {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [tipsData, setTipsData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [tipsWithdrawing, setTipsWithdrawing] = useState(false);

  useEffect(() => { fetchWalletData(); }, []);

  const fetchWalletData = async () => {
    try {
      const [walletRes, txnRes, tipsRes] = await Promise.all([
        axios.get(`${API}/wallet/balance`, { withCredentials: true }),
        axios.get(`${API}/wallet/transactions`, { withCredentials: true }),
        axios.get(`${API}/professional/tips`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setWallet(walletRes.data);
      setTransactions(txnRes.data.transactions || []);
      if (tipsRes.data) setTipsData(tipsRes.data);
    } catch (err) {
      toast.error("Failed to load wallet data");
    }
    setLoading(false);
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 5) { toast.error("Minimum withdrawal is $5.00"); return; }
    if (amount > (wallet?.balance || 0)) { toast.error("Insufficient balance"); return; }

  const handleTipsWithdraw = async () => {
    setTipsWithdrawing(true);
    try {
      const res = await axios.post(`${API}/professional/tips/withdraw`, {}, { withCredentials: true });
      toast.success(`$${res.data.amount.toFixed(2)} tips transferred to your Stripe account!`);
      fetchWalletData();
    } catch (err) {
      toast.error(getErrorMessage(err, "Tip withdrawal failed. Make sure your Stripe account is set up."));
    } finally {
      setTipsWithdrawing(false);
    }
  };
    setWithdrawing(true);
    try {
      const formData = new FormData();
      formData.append("amount", amount);
      await axios.post(`${API}/wallet/withdraw`, formData, { withCredentials: true });
      toast.success(`$${amount.toFixed(2)} transferred to your Stripe account`);
      setWithdrawAmount("");
      fetchWalletData();
    } catch (err) {
      toast.error(getErrorMessage(err, "Withdrawal failed. Make sure your Stripe account is set up."));
    }
    setWithdrawing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50"><Navbar />
        <div className="flex items-center justify-center pt-40"><DollarSign className="w-8 h-8 animate-pulse text-emerald-600" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="wallet-page">
      <Navbar />
      <section className="relative pt-32 pb-12">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950 via-emerald-900 to-emerald-950" />
        <div className="section-container relative z-10">
          <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-emerald-300 hover:text-white mb-4 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Wallet className="w-8 h-8" /> Wallet & Earnings
          </h1>
        </div>
      </section>

      <section className="py-8 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Balance Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl p-6 text-white">
            <p className="text-emerald-200 text-sm mb-1">Available Balance</p>
            <p className="text-4xl font-bold mb-6" data-testid="wallet-balance">${(wallet?.balance || 0).toFixed(2)}</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-emerald-200 text-xs">Total Earned</p>
                <p className="font-semibold text-lg" data-testid="total-earned">${(wallet?.total_earned || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-emerald-200 text-xs">Platform Fees</p>
                <p className="font-semibold text-lg" data-testid="total-fees">${(wallet?.total_platform_fees || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-emerald-200 text-xs">Withdrawn</p>
                <p className="font-semibold text-lg" data-testid="total-withdrawn">${(wallet?.total_withdrawn || 0).toFixed(2)}</p>
              </div>
            </div>
          </motion.div>

          {/* Stripe Connect Setup */}
          {!user?.stripe_connect_id && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-800 mb-2">Set Up Payout Account</h3>
              <p className="text-sm text-amber-700 mb-4">Connect your Stripe account to withdraw earnings directly to your bank.</p>
              <Link to="/professional/dashboard">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="setup-stripe-btn">
                  Set Up Stripe Payouts
                </Button>
              </Link>
            </motion.div>
          )}

          {/* Tips Balance */}
          {tipsData && tipsData.withdrawable_balance > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-6" data-testid="tips-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-amber-800">Tips Balance</h3>
                  <p className="text-xs text-amber-600">100% of tips go to you — no platform fee</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-amber-700" data-testid="tips-withdrawable">${tipsData.withdrawable_balance.toFixed(2)}</p>
                  <p className="text-xs text-amber-500">Available to withdraw</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-emerald-950">${tipsData.tips_today.toFixed(2)}</p>
                  <p className="text-xs text-stone-500">Today</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-emerald-950">${tipsData.tips_all_time.toFixed(2)}</p>
                  <p className="text-xs text-stone-500">All Time</p>
                </div>
              </div>
              {tipsData.withdrawable_balance >= 1 && (
                <Button
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={handleTipsWithdraw}
                  disabled={tipsWithdrawing}
                  data-testid="withdraw-tips-btn"
                >
                  {tipsWithdrawing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Withdrawing Tips...</>
                  ) : (
                    <>Withdraw ${tipsData.withdrawable_balance.toFixed(2)} Tips</>
                  )}
                </Button>
              )}
            </motion.div>
          )}

          {/* Withdraw Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-6 border border-stone-100">
            <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-emerald-600" /> Withdraw Funds
            </h3>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  type="number"
                  min="5"
                  step="0.01"
                  placeholder="Enter amount (min $5.00)"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className="pl-9"
                  data-testid="withdraw-amount-input"
                />
              </div>
              <Button onClick={handleWithdraw} disabled={withdrawing || !withdrawAmount} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6" data-testid="withdraw-btn">
                {withdrawing ? "Processing..." : "Withdraw"}
              </Button>
            </div>
            <p className="text-xs text-stone-400 mt-2">Funds are transferred instantly to your connected Stripe account</p>
          </motion.div>

          {/* Transactions */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-6 border border-stone-100">
            <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-600" /> Transaction History
            </h3>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <TrendingUp className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                <p className="text-stone-500">No transactions yet</p>
                <p className="text-stone-400 text-sm">Complete jobs to start earning</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((txn) => (
                  <div key={txn.transaction_id} className="flex items-center justify-between py-3 border-b border-stone-50 last:border-0" data-testid={`txn-${txn.transaction_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${txn.type === "earning" ? "bg-green-100" : "bg-amber-100"}`}>
                        {txn.type === "earning" ? <ArrowDownLeft className="w-5 h-5 text-green-600" /> : <ArrowUpRight className="w-5 h-5 text-amber-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-800">{txn.description}</p>
                        <p className="text-xs text-stone-400">{new Date(txn.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${txn.amount >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {txn.amount >= 0 ? "+" : ""}${Math.abs(txn.amount).toFixed(2)}
                      </p>
                      {txn.platform_fee > 0 && (
                        <p className="text-xs text-stone-400 flex items-center gap-0.5 justify-end"><Percent className="w-3 h-3" /> ${txn.platform_fee.toFixed(2)} fee</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default WalletPage;
